import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutMission, aboutVision, aboutJourney, aboutValues, aboutTeam, aboutPartners, aboutRecognition } from '@/mocks/aboutData';

function ScrollReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`scroll-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative pt-20 pb-20 md:pt-28 md:pb-28 bg-gradient-to-br from-primary-500 via-primary-400 to-accent-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-information-line mr-1.5"></i>Our Story
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              About Happy Faces Society
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Since 2012, we have been dedicated to transforming lives through transparent, impactful, and sustainable community development programs across India.
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <ScrollReveal>
                <div className="space-y-6">
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wider">Our Mission</span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">{aboutMission.heading}</h2>
                  <p className="text-foreground-600 text-base leading-relaxed">{aboutMission.content}</p>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-background-100">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-double-line text-primary-600 text-lg"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">Transparent Operations</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-background-100">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-heart-line text-accent-600 text-lg"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">Community First</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-background-100">
                      <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-bar-chart-box-line text-secondary-600 text-lg"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">Measurable Impact</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-background-100">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-earth-line text-primary-600 text-lg"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">Nationwide Reach</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={150}>
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                    <img src={aboutMission.imageUrl} alt="Our Mission" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-sm border border-background-200 max-w-[220px]">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                        <i className="ri-flag-line text-white text-lg"></i>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-foreground-950">12+</div>
                        <div className="text-xs text-foreground-500">Years of Impact</div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mt-20 lg:mt-28">
              <ScrollReveal delay={100}>
                <div className="relative lg:order-2">
                  <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                    <img src={aboutVision.imageUrl} alt="Our Vision" className="w-full h-full object-cover" />
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal>
                <div className="space-y-6 lg:order-1">
                  <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full uppercase tracking-wider">Our Vision</span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">{aboutVision.heading}</h2>
                  <p className="text-foreground-600 text-base leading-relaxed">{aboutVision.content}</p>
                  <div className="space-y-3 pt-2">
                    {['Quality education for every child', 'Accessible healthcare for all', 'Empowered women leading communities', 'Sustainable and thriving ecosystems'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-s-line text-accent-600 text-sm"></i>
                        </div>
                        <span className="text-foreground-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Our Journey */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <ScrollReveal className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Our Journey</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">From Humble Beginnings</h2>
              <p className="text-foreground-500 text-base max-w-xl mx-auto">A decade of growth, impact, and transformation — from sponsoring 10 children to impacting 25,000+ lives.</p>
            </ScrollReveal>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px hidden md:block"></div>
              <div className="space-y-0">
                {aboutJourney.map((item, i) => (
                  <ScrollReveal key={item.year} delay={i * 80}>
                    <div className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 py-6 md:py-8 relative ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}>
                      <div className="hidden md:flex md:w-1/2"></div>
                      <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-100 z-10"></div>
                      <div className="md:w-1/2 pl-10 md:pl-0">
                        <div className={`bg-white rounded-2xl p-5 md:p-6 border border-background-200 ${
                          i % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                        }`}>
                          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full mb-2">
                            {item.year}
                          </span>
                          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">{item.title}</h3>
                          <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <ScrollReveal className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Our Values</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">What We Stand For</h2>
              <p className="text-foreground-500 text-base max-w-xl mx-auto">Six core principles that guide every decision, every program, and every rupee we spend.</p>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutValues.map((value, i) => (
                <ScrollReveal key={value.title} delay={i * 100}>
                  <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200 h-full hover:border-primary-200 transition-all duration-300 group">
                    <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                      <i className={`${value.icon} text-primary-600 text-xl group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{value.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{value.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <ScrollReveal className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Our Team</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">The People Behind the Mission</h2>
              <p className="text-foreground-500 text-base max-w-xl mx-auto">A passionate team of social entrepreneurs, doctors, educators, and community organizers dedicated to creating lasting change.</p>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutTeam.map((member, i) => (
                <ScrollReveal key={member.name} delay={i * 80}>
                  <div className="bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 group h-full">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={member.imageUrl} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                      <p className="text-primary-600 text-xs font-semibold mb-2">{member.role}</p>
                      <p className="text-foreground-500 text-xs leading-relaxed">{member.bio}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Partners */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <ScrollReveal className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Our Partners</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Trusted by Leading Organizations</h2>
              <p className="text-foreground-500 text-base max-w-xl mx-auto">We collaborate with philanthropies, corporations, and foundations who share our vision of a better India.</p>
            </ScrollReveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutPartners.map((partner, i) => (
                <ScrollReveal key={partner.name} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-5 md:p-6 border border-background-200 hover:border-primary-200 transition-all duration-300 group text-center h-full flex flex-col items-center justify-center">
                    <div className="w-full h-16 mb-3 overflow-hidden">
                      <img src={partner.logoUrl} alt={partner.name} className="w-full h-full object-contain" />
                    </div>
                    <span className="text-foreground-800 text-sm font-medium">{partner.name}</span>
                    <span className="text-foreground-400 text-xs mt-0.5">{partner.category}</span>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Recognition */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <ScrollReveal className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Recognition</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Awards & Accolades</h2>
              <p className="text-foreground-500 text-base max-w-xl mx-auto">Our work has been recognized by governments, industry bodies, and international organizations.</p>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutRecognition.map((item, i) => (
                <ScrollReveal key={item.title} delay={i * 100}>
                  <div className="bg-white rounded-2xl p-6 border border-background-200 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-line text-accent-600 text-lg"></i>
                      </div>
                      <span className="text-accent-600 text-sm font-bold">{item.year}</span>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary-500 via-primary-400 to-accent-400 p-10 md:p-16 text-center">
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>
              <div className="relative z-10">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Join Us in Making a Difference</h2>
                <p className="text-white/80 text-base max-w-lg mx-auto mb-8">Whether you donate, volunteer, or partner with us — every contribution helps build a brighter future for those who need it most.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/donate" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-primary-600 text-sm font-semibold rounded-full hover:bg-white/90 transition-all duration-300 whitespace-nowrap">
                    <i className="ri-heart-line text-base"></i>
                    Donate Now
                  </Link>
                  <Link to="/volunteer" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full hover:bg-white/30 transition-all duration-300 whitespace-nowrap border border-white/30">
                    <i className="ri-team-line text-base"></i>
                    Become a Volunteer
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}